/**
 * PDP media carousel: media switching for sections/pdp-media-carousel.liquid.
 *
 * Docs: docs/sections/pdp-media-carousel.md
 *
 * The cards are a standard <swipper-slider> (assets/section-swipper-slider.js),
 * which owns the Swiper, the dots, the arrows and their labels. This element
 * only follows it: the slider announces each slide change with a
 * `swipper-slider:change` event, and the media frame that matches the active
 * card is shown. The video moves with it: the active frame gets its iframe and
 * every other frame loses its own, so one video is loaded at a time and a video
 * stops when you leave its slide.
 *
 * @extends HTMLElement
 */
if (!customElements.get('pdp-media-carousel')) {
  customElements.define(
    'pdp-media-carousel',
    class PdpMediaCarousel extends HTMLElement {
      constructor() {
        super();
        this.onSlideChange = this.onSlideChange.bind(this);
      }

      connectedCallback() {
        this.frames = Array.from(this.querySelectorAll('[data-frame]'));
        this.counter = this.querySelector('[data-current]');
        this.addEventListener('swipper-slider:change', this.onSlideChange);
      }

      disconnectedCallback() {
        this.removeEventListener('swipper-slider:change', this.onSlideChange);
      }

      /**
       * @param {CustomEvent<{ index: number }>} event
       */
      onSlideChange(event) {
        const index = event.detail?.index ?? 0;

        for (const [frameIndex, frame] of this.frames.entries()) {
          const isActive = frameIndex === index;
          frame.hidden = !isActive;

          if (isActive) {
            this.mountVideo(frame);
          } else {
            frame.querySelector('iframe')?.remove();
          }
        }

        if (this.counter) {
          this.counter.textContent = index + 1;
        }
      }

      /**
       * Adds the iframe to a video frame. The link was checked in Liquid, so
       * only a YouTube embed link can be here.
       * @param {HTMLElement} frame
       */
      mountVideo(frame) {
        const url = frame.dataset.videoUrl;
        if (!url || frame.querySelector('iframe')) return;

        const iframe = document.createElement('iframe');
        iframe.className = 'pdp-media-carousel__video';
        iframe.src = url;
        iframe.title = frame.dataset.videoTitle || '';
        iframe.loading = 'lazy';
        iframe.allow = 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allowFullscreen = true;
        frame.append(iframe);
      }
    }
  );
}
